import {
  keepPreviousData,
  noop,
  queryOptions,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useCallback } from 'react'

import { livePolling } from '@/app/query-client'
import { depthKeys } from '@/features/depth/api/keys'
import { depthService } from '@/features/depth/api/depthService'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type { DepthCompany, DepthPage, DepthQuery, DepthSector } from '@/schemas/depth'

/**
 * The listed universe changes when a company lists, not when a price moves, so
 * the sector list and each sector's names outlive every poll of the books.
 */
const LISTING_STALE_MS = 60 * 60_000

/**
 * The order books.
 *
 * Polled while NEPSE trades, because depth is the one thing on the Market
 * pages that changes without a trade: an order placed or cancelled moves a
 * rung, and a stale book is worse than no book — it shows resting interest
 * that is no longer there. The gate still applies, so an overnight tab issues
 * nothing.
 */
/**
 * Key + fetch only, so `DepthScreen.loader.ts` can warm the same cache entry
 * `useMarketDepth` reads on mount. The loader only ever reaches this factory
 * once its own market-status read says OPEN — an imperative `client.query`
 * call has no `enabled` of its own to honour the gate below, so this must
 * never be awaited blind; see that loader's doc comment for the 409 race it
 * still has to account for even so.
 */
export const marketDepthQueryOptions = (query: DepthQuery) =>
  queryOptions<DepthPage>({
    queryKey: depthKeys.book(query),
    queryFn: () => depthService.depth(query),
  })

export const useMarketDepth = (query: DepthQuery): UseQueryResult<DepthPage> => {
  const { isOpen, isPending } = useMarketStatus()

  return useQuery<DepthPage>({
    ...marketDepthQueryOptions(query),
    // Gated on the status the app already polls for, not fired blind: mm-be
    // answers depth with a 409 MARKET_CLOSED for the entire closed session,
    // so a request here would never do anything but reproduce that error.
    // `!isPending`, not just `isOpen`, because the status's own first fetch
    // has not landed yet either — firing on the "closed" default a query
    // reports before it has any data would be the same wrong guess.
    enabled: !isPending && isOpen,
    refetchInterval: livePolling(isOpen),
    placeholderData: keepPreviousData,
  })
}

/**
 * The sector select — ids and names only, one request. Live market, 52-week
 * and Movers draw it as a filter and never offer a sector's names, so none of
 * them pays for those.
 *
 * `staleTime` lives here rather than only on the hook below, because every
 * loader that starts this read (`DepthScreen.loader.ts`, `MoversScreen.loader.ts`,
 * `Week52Screen.loader.ts`, `LiveMarketScreen.loader.ts`) does it through
 * `prefetchRouteData`, which honours a query's OWN `staleTime` rather than
 * whatever `useDepthSectors` happens to ask for later — a hook-only
 * `staleTime` would never reach that imperative `client.query` call, and the
 * filter's own names would refetch on every one of those pages' visits
 * instead of the hour this list actually lives for.
 *
 * All FOUR loaders above start this unawaited: it is secondary everywhere,
 * including on `/market/depth` itself now — see that loader's own doc
 * comment for what IS main there.
 */
export const depthSectorsQueryOptions = () =>
  queryOptions<DepthSector[]>({
    queryKey: depthKeys.sectors(),
    queryFn: ({ client }) => depthService.sectors(client),
    staleTime: LISTING_STALE_MS,
  })

export const useDepthSectors = (): UseQueryResult<DepthSector[]> =>
  useQuery(depthSectorsQueryOptions())

const depthSectorCompaniesQueryOptions = (sectorId: string) =>
  queryOptions({
    queryKey: depthKeys.sectorCompanies(sectorId),
    queryFn: () => depthService.sectorCompanies(sectorId),
    staleTime: LISTING_STALE_MS,
  })

/** The names the company picker offers inside one sector. */
export const useDepthSectorCompanies = (sectorId: string): UseQueryResult<DepthCompany[]> =>
  useQuery(depthSectorCompaniesQueryOptions(sectorId))

/**
 * Starts every sector's names at once — from the sector menu opening, the
 * moment a member is about to choose, so whichever sector they pick fills the
 * company picker from cache.
 *
 * `client.query` answers a fresh entry from cache, so a menu reopened inside
 * the hour asks for nothing. A failure is swallowed here and belongs to the
 * picker, which shows it only if that sector is chosen and retries through its
 * own query. `retry: false`, or a server that is down would cost three
 * requests per sector for a list nobody has asked to see yet.
 */
export const useDepthSectorCompaniesPrefetch = (): ((sectorIds: readonly string[]) => void) => {
  const client = useQueryClient()

  return useCallback(
    (sectorIds: readonly string[]) => {
      for (const sectorId of sectorIds) {
        client.query({ ...depthSectorCompaniesQueryOptions(sectorId), retry: false }).catch(noop)
      }
    },
    [client],
  )
}
